import { CSSProperties, ReactNode, createElement } from 'react';
import { useTranslation } from 'react-i18next';

// Type/Interface to avoid unwanted tags such as 'divs' e.g.
type TagVariants =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'label';

type TypographyProps = {
  tag: TagVariants;
  children: ReactNode | string;
  type: typographyStyles;
  color?: keyof typeof colors;
  addClass?: string | null;
  noTranslation?: boolean;
};

export type typographyStyles =
  | 'smallest'
  | 'small'
  | 'label'
  | 'labelSemiBold'
  | 'default'
  | 'semibold'
  | 'bold'
  | 'faded'
  | 'accent'
  | 'subtitle'
  | 'title'
  | 'header';

const style = {
  smallest: 'text-[10px] opacity-75',
  small: 'text-[12px]',
  label: 'text-sm',
  labelSemiBold: 'text-sm font-semibold',
  default: 'text-base dark:text-gray-200',
  semibold: 'dark:text-slate-200 text-slate-800 font-semibold',
  bold: 'dark:text-slate-200 text-slate-800 font-bold',
  faded: 'opacity-50',
  accent: 'text-blue-700',
  subtitle: 'text-neutral-700 font-bold dark:text-stone-100 text-xl',
  title: 'text-neutral-700 font-bold text-xl dark:text-stone-300',
  header:
    'text-neutral-700 dark:text-stone-300 font-semibold text-4xl leading-none',
  softHeader:
    'text-neutral-700 dark:text-stone-300 font-light text-3xl leading-none leading-10',
};

const colors = {
  default: 'dark:text-slate-300 text-slate-600',
  success: 'text-green-500',
  white: 'text-white',
  danger: 'text-red-500',
  blue: 'text-blue-700',
  noColor: '',
};

export const Typography = ({
  tag,
  children,
  type,
  color = 'default',
  addClass,
  noTranslation = false,
}: TypographyProps & CSSProperties) => {
  const { t } = useTranslation();

  return createElement(
    tag,
    { className: `${addClass} ${style[type]} ${colors[color]}` },
    typeof children === 'string' && !noTranslation ? t(children) : children
  );
};

export default Typography;
